import { useState } from 'react';
import { useThreadRuntime } from '@assistant-ui/react';
import type { ActionOrigin } from './action-confirmation';
import type { ActionSurfaceCopy } from './action-surface-copy';

/**
 * The request that produced this answer, exactly as it was sent: the user message
 * directly before the answer that staged the action. Nothing is rebuilt from the card.
 */
export function boundRequest(
  messages: readonly {
    id: string;
    role: string;
    content: readonly unknown[];
    attachments?: readonly unknown[];
  }[],
  answerId: string,
): string | null {
  const at = messages.findIndex((message) => message.id === answerId);
  const asked = at > 0 ? messages[at - 1] : null;
  if (!asked || asked.role !== 'user' || asked.attachments?.length) return null;
  const parts = asked.content as { type: string; text?: string }[];
  if (!parts.length || parts.some((part) => part.type !== 'text' || typeof part.text !== 'string'))
    return null;
  const text = parts.map((part) => part.text).join('');
  return text.trim() ? text : null;
}

function shown(element: HTMLElement): boolean {
  if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  const view = element.ownerDocument.defaultView;
  const style =
    typeof view?.getComputedStyle === 'function' ? view.getComputedStyle(element) : null;
  return !style || (style.display !== 'none' && style.visibility !== 'hidden');
}

/**
 * The message box of the visible chat: inside a shown chat slot first, then (standalone Chat has
 * no workspace slot) any shown composer. A hidden page's composer is never the target.
 */
export function findComposer(root: Document): HTMLTextAreaElement | null {
  const inSlots = [
    ...root.querySelectorAll<HTMLTextAreaElement>('.ws-chat-slot .aui-composer-input'),
  ];
  const anywhere = [...root.querySelectorAll<HTMLTextAreaElement>('.aui-composer-input')];
  return inSlots.find(shown) ?? anywhere.find(shown) ?? null;
}

/** After React has put the filled text in the box, so the caret lands at its end. */
export function focusComposer() {
  requestAnimationFrame(() => {
    const input = findComposer(document);
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  });
}

type ComposerLike = {
  getState(): { text: string; attachments: readonly unknown[] };
  setText(text: string): void;
};
type ThreadLike = {
  composer: ComposerLike;
  getState(): { messages: readonly { id: string }[] };
  getMessageByIndex(index: number): { composer: { getState(): { isEditing: boolean } } };
};

/**
 * Puts the bound request in the thread's message box only when it is empty and no earlier
 * message is being edited; anything else is left exactly as the person has it.
 * (The card sits inside a message, so useComposerRuntime would be that message's edit composer.)
 */
export function fillMessageBox(thread: ThreadLike, original: string): boolean {
  const box = thread.composer.getState();
  const count = thread.getState().messages.length;
  let editing = false;
  for (let index = 0; index < count; index += 1)
    if (thread.getMessageByIndex(index).composer.getState().isEditing) editing = true;
  if (box.text !== '' || box.attachments.length > 0 || editing) return false;
  thread.composer.setText(original);
  return true;
}

/** After an expired approval: never sends or restages; only puts words in the message box. */
export function AskAgain({
  origin,
  copy,
}: {
  origin?: ActionOrigin;
  copy: Pick<
    ActionSurfaceCopy,
    'askAgain' | 'writeNewRequest' | 'askAgainPrefilled' | 'askAgainDraftKept'
  >;
}) {
  const thread = useThreadRuntime({ optional: true });
  const [note, setNote] = useState('');
  const original =
    origin && thread ? boundRequest(thread.getState().messages, origin.messageId) : null;
  const act = () => {
    if (thread && original)
      setNote(fillMessageBox(thread, original) ? copy.askAgainPrefilled : copy.askAgainDraftKept);
    focusComposer();
  };
  return (
    <>
      <button type="button" className="pc-action-ask-again" onClick={act}>
        {original ? copy.askAgain : copy.writeNewRequest}
      </button>
      <span className="pc-action-ask-again-note" role="status">
        {note}
      </span>
    </>
  );
}
